import React, { FC, Fragment, useCallback, useState } from 'react';
import {
    Alert,
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { getGroupsTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import GroupsPageActions from './groups-page-actions.component';
import AddGroupDialog from './add-group-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import EditGroupDialog from './edit-group-dialog.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteGroupTemplate } from 'apollo/templates/action';

const Groups: FC = () => {
    const { t: translate } = useTranslation(['lab-settings']);
    const [groups, setGroups] = useState<{ _id: string; name: string }[]>([]);
    const [filters, setFilters] = useState<{ freetext: string }>({ freetext: '' });
    const [groupToEdit, setGroupToEdit] = useState<{ _id: string; name: string }>({} as { _id: string; name: string });
    const { showBoundary } = useDenthubErrorBoundary();
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteGroupConfirmDialogOpen, setDeleteGroupConfirmDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState({ addedGroup: false, updatedGroup: false, deletedGroup: false });
    const [groupId, setGroupId] = useState('');
    const [error, setError] = useState<Error | null>(null);

    const { loading: groupsLoading, refetch } = useQuery(getGroupsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: filters,
        },
        onCompleted({ groups }) {
            setGroups(groups);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteGroup] = useMutation(deleteGroupTemplate, {
        onCompleted: () => {
            setDeleteGroupConfirmDialogOpen(false);
            setGroupId('');
            setSnackbarOpen({ ...snackbarOpen, deletedGroup: true });
            refetch();
        },
        onError: (err: ApolloError) => {
            setDeleteGroupConfirmDialogOpen(false);
            setGroupId('');
            const [firstError] = err.graphQLErrors;
            const errorType = (firstError?.extensions?.exception as any)?.otherInfo?.errorType;
            if (errorType === 'GROUP_EXISTS_IN_ACTION') {
                setError(new Error(translate(`lab-settings:${errorType}`)));
            } else {
                showBoundary(err);
            }
        },
    });

    const handleFilterChange = useCallback(
        debounce((filters: { freetext: string }) => {
            setFilters({ freetext: filters.freetext });
        }, 300),
        [],
    );

    const handleEditGroup = (category: { _id: string; name: string }) => {
        setGroupToEdit(category);
        setEditDialogOpen(true);
    };

    const editGroup = (group: { _id: string; name: string }) => {
        const groupArr = [...groups];
        const index = groupArr.findIndex((cat: { _id: string; name: string }) => cat._id === group._id);
        if (index > -1) {
            groupArr.splice(index, 1, group);
        }
        setGroups([...groupArr]);
        setSnackbarOpen({ ...snackbarOpen, updatedGroup: true });
    };

    const handleDeleteClick = (jobId: string) => {
        setGroupId(jobId as string);
        setDeleteGroupConfirmDialogOpen(true);
        setError(null);
    };

    const handleCloseDialog = () => {
        setDeleteGroupConfirmDialogOpen(false);
        setGroupId('');
        setError(null);
    };

    const handleConfirmDelete = () => {
        deleteGroup({ variables: { groupId } });
    };

    return (
        <Fragment>
            {error ? <AlertSnackbar type="error" message={error?.message || ''} /> : undefined}
            <GroupsPageActions onNewGroupClick={() => setAddDialogOpen(true)} onFilterChange={handleFilterChange} />
            {groupsLoading ? (
                <Loader />
            ) : (
                <Fragment>
                    {!groups.length ? (
                        <Box mt={2}>
                            <Alert severity="info">{translate('lab-settings:NO_GROUPS')}</Alert>
                        </Box>
                    ) : (
                        <Paper variant="outlined">
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{translate('lab-settings:GROUP')}</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {groups.map((group) => (
                                            <TableRow
                                                key={group._id}
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleEditGroup(group)}
                                            >
                                                <TableCell>{group.name}</TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        sx={{ float: 'right' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteClick(group._id);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                    <AddGroupDialog
                        dialogOpen={addDialogOpen}
                        onCloseDialog={() => setAddDialogOpen(false)}
                        onAddGroup={(group: { _id: string; name: string }) => {
                            setSnackbarOpen({ ...snackbarOpen, addedGroup: true });
                            setGroups([...groups, group]);
                        }}
                    />
                    <EditGroupDialog
                        dialogOpen={editDialogOpen}
                        onCloseDialog={() => setEditDialogOpen(false)}
                        groupToEdit={groupToEdit}
                        onEditGroup={editGroup}
                    />
                    <ConfirmDialog
                        open={deleteGroupConfirmDialogOpen || false}
                        handleClose={handleCloseDialog}
                        dialogTitle={translate('lab-settings:DELETE_GROUP')}
                        dialogContent={translate('lab-settings:CONFIRM_DELETE_GROUP')}
                        confirmBtnLabel={translate('lab-settings:DELETE_GROUP')}
                        handleConfirm={handleConfirmDelete}
                    />
                    {snackbarOpen.addedGroup ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:GROUP_ADDED')} />
                    ) : undefined}
                    {snackbarOpen.updatedGroup ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:GROUP_UPDATED')} />
                    ) : undefined}
                    {snackbarOpen.deletedGroup ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:GROUP_DELETED')} />
                    ) : undefined}
                </Fragment>
            )}
        </Fragment>
    );
};

export default Groups;
